import { z } from "zod";
import {
  isReportRangeBound,
  MAX_REPORT_YEAR,
  MIN_REPORT_YEAR,
  REPORT_DATE_PRESET_VALUES,
} from "../reports/date-range";

/**
 * Reports Dashboard — global filter contract.
 *
 * One shared object threaded hook → route → controller → action → AI prompt so
 * that every KPI / chart / export / summary responds to the SAME filters
 * (PRD "Reports Dashboard" §10; acceptance criterion 52). This zod schema is the
 * single source of truth; `ReportsDashboardFilters` is inferred from it.
 */

/** The ONE preset list (`../reports/date-range.ts`) — never re-list it here. */
export const reportsDateRangePresetSchema = z.enum(REPORT_DATE_PRESET_VALUES);

export const participantSelectionModeSchema = z.enum([
  "one",
  "selected",
  "group",
  "all",
]);

export const staffSelectionModeSchema = z.enum(["one", "selected", "all"]);

export const participantGroupKeySchema = z.enum([
  "site",
  "service_type",
  "plan_manager",
  "support_coordinator",
  "participant_status",
]);

export const reportCategorySchema = z.enum([
  "overview",
  "finance",
  "participants",
  "scheduling",
  "team",
  "compliance",
  "custom",
]);

/**
 * One end of a custom range: a real calendar day (or an ISO instant starting
 * with one) within the supported years. A free string used to reach the trend
 * axis, where a range ending in December 9999 walked forever and froze the API
 * for every organisation (PR #511 review F25).
 */
const reportRangeBoundSchema = z.string().refine(isReportRangeBound, {
  message: `Use a real date (YYYY-MM-DD) between ${MIN_REPORT_YEAR} and ${MAX_REPORT_YEAR}`,
});

export const reportStatusFilterSchema = z.enum([
  "draft",
  "published",
  "completed",
  "cancelled",
  "missing_notes",
  "approved",
  "unapproved",
  "voided",
  "overdue",
  "open",
  "closed",
]);

export const reportsDashboardFiltersSchema = z.object({
  dateRange: z
    .object({
      preset: reportsDateRangePresetSchema.default("this_month"),
      from: reportRangeBoundSchema.optional(),
      to: reportRangeBoundSchema.optional(),
    })
    .default({ preset: "this_month" }),
  participants: z
    .object({
      mode: participantSelectionModeSchema.default("all"),
      ids: z.array(z.string()).optional(),
      group: participantGroupKeySchema.optional(),
    })
    .default({ mode: "all" }),
  staff: z
    .object({
      mode: staffSelectionModeSchema.default("all"),
      ids: z.array(z.string()).optional(),
    })
    .default({ mode: "all" }),
  teamId: z.string().optional(),
  serviceType: z.string().optional(),
  /** Organisation region id — scopes to participants in that region (resolved to clientIds). */
  region: z.string().optional(),
  reportCategory: reportCategorySchema.optional(),
  status: z.array(reportStatusFilterSchema).optional(),
});

export type ReportsDashboardFilters = z.infer<
  typeof reportsDashboardFiltersSchema
>;
